// Central API auth layer.
//
// Every request to our backend must carry a Firebase ID token as
// `Authorization: Bearer <idToken>` (the backend rejects anything else with 401).
// Rather than edit ~240 individual fetch() call sites — where a single missed one
// would silently 401 in production — we install one global fetch interceptor that:
//   1. Attaches a fresh Firebase ID token to requests targeting our API (BASE_URL).
//   2. Leaves all other requests (data: URLs, blobs, third-party) untouched, so we
//      never leak our token to other origins.
//   3. On a 401, force-refreshes the token once and retries; if it still fails,
//      routes the user back to /login.
//
// New code can also call the exported `authFetch` explicitly; it just delegates to
// the (patched) global fetch.

import { auth } from "./auth";

const BASE_URL = process.env.REACT_APP_API_URL;

function urlString(input) {
  if (typeof input === "string") return input;
  if (input && typeof input.url === "string") return input.url; // Request object
  try {
    return String(input);
  } catch {
    return "";
  }
}

// Only our own backend gets the bearer token.
function isApiRequest(input) {
  if (!BASE_URL) return false;
  return urlString(input).startsWith(BASE_URL);
}

let redirecting = false;
function redirectTo(path) {
  if (redirecting) return;
  if (typeof window === "undefined") return;
  if (window.location.pathname === path) return;
  redirecting = true; // reset naturally on the ensuing full page load
  window.location.assign(path);
}

function redirectToLogin() {
  redirectTo("/login");
}

// Sliding session keep-alive.
//
// The backend now requires a live ris_sessions row on every request (that is
// what makes the MFA challenge enforceable), and that row has an idle timeout.
// Without renewal a user would be logged out mid-shift, so any successful API
// call refreshes the session — but at most once every REFRESH_INTERVAL_MS, so a
// busy screen doesn't add a request per action.
//
// This only slides the idle window. The server clamps every renewal to the
// session's absolute_expiration, which is fixed at login and cannot be pushed
// out from here, so the session still dies at a known time.
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;
let lastRefreshAt = 0;
let refreshInFlight = false;

async function maybeRefreshSession(nativeFetch, user) {
  if (!BASE_URL) return;
  if (refreshInFlight) return;
  if (Date.now() - lastRefreshAt < REFRESH_INTERVAL_MS) return;

  const sessionToken = sessionStorage.getItem("sessionToken");
  if (!sessionToken) return;

  refreshInFlight = true;
  try {
    const token = await user.getIdToken(false);
    const res = await nativeFetch(`${BASE_URL}/RIS/refreshSession`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "X-Session-Token": sessionToken,
      },
    });
    // Only treat a success as "recently refreshed"; a failure should be retried
    // on the next request rather than suppressed for another ten minutes.
    if (res.ok) lastRefreshAt = Date.now();
  } catch {
    /* keep-alive is best-effort — never surface it to the caller */
  } finally {
    refreshInFlight = false;
  }
}

// Wait for Firebase to finish restoring any persisted session before concluding
// the user is unauthenticated — avoids false redirects on a page reload.
async function getCurrentUser() {
  if (auth.currentUser) return auth.currentUser;
  if (typeof auth.authStateReady === "function") {
    try {
      await auth.authStateReady();
    } catch {
      /* fall through */
    }
  }
  return auth.currentUser;
}

export function installAuthFetch() {
  if (typeof window === "undefined" || window.__authFetchInstalled) return;

  const nativeFetch = window.fetch.bind(window);
  window.__nativeFetch = nativeFetch;

  window.fetch = async (input, init = {}) => {
    if (!isApiRequest(input)) {
      return nativeFetch(input, init);
    }

    const user = await getCurrentUser();
    if (!user) {
      redirectToLogin();
      throw new Error("Not authenticated");
    }

    const send = async (forceRefresh) => {
      const token = await user.getIdToken(forceRefresh);
      // Preserve any caller-supplied headers (e.g. Content-Type for JSON; for
      // FormData uploads no Content-Type is set so the browser keeps the
      // multipart boundary) and add the Authorization header.
      const headers = new Headers(init.headers || undefined);
      headers.set("Authorization", `Bearer ${token}`);
      const sessionToken = sessionStorage.getItem("sessionToken");
      if (sessionToken) headers.set("X-Session-Token", sessionToken);
      return nativeFetch(input, { ...init, headers });
    };

    let res = await send(false);
    if (res.status === 401) {
      // Either the Firebase ID token expired, or the server-side session did.
      // Force-refresh the token and retry once; a second 401 means the session
      // is gone (idle timeout, absolute ceiling, or an admin revoked it) and the
      // user has to sign in again.
      res = await send(true);
      if (res.status === 401) {
        redirectToLogin();
        return res;
      }
    }

    // The account owes MFA enrolment: the server issued an enrolment-only
    // session that reaches nothing but the setup endpoints.
    if (res.status === 403) {
      try {
        const body = await res.clone().json();
        if (body?.code === "MFA_SETUP_REQUIRED") {
          redirectTo("/mfa-enforce-setup");
          return res;
        }
      } catch {
        /* not JSON, or already consumed — fall through and return as-is */
      }
    }

    if (res.ok) {
      // Fire-and-forget: never delays the caller's response.
      maybeRefreshSession(nativeFetch, user);
    }

    return res;
  };

  window.__authFetchInstalled = true;
}

// Explicit wrapper for new code; delegates to the (patched) global fetch.
export const authFetch = (input, init) => window.fetch(input, init);
